import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCartPricing } from '../contexts/CartPricingContext';
import { useLocale } from '../contexts/LocaleContext';
import CheckoutContext from '../lib/CheckoutContext';
import { getFreeTrialCartItemIds } from '../lib/cart';
import { getProductsAndVariantsBySku } from '../lib/product';
import { getLocalizedPricing } from '../lib/util/money';
import * as types from '../types';

/**
 * List price of the hardware-first cart's lines that are neither repriced trackers nor trial units, e.g. a band.
 * The server charges them at catalog price inside its total, so the receipt has to list them for its rows to add up.
 */
export default function useHardwareFirstAccessoriesInCents(): number {
  const { cart } = useContext(CheckoutContext);
  const products = useSelector((state: types.AppState) => state.config.products);
  const { hardwareDiscounts } = useCartPricing();
  const { currency } = useLocale();

  return useMemo(() => {
    const skuMap = getProductsAndVariantsBySku(products);
    const trialIds = new Set(getFreeTrialCartItemIds(cart, products));
    return Object.values(cart.cartItems)
      .filter((item) => !hardwareDiscounts?.[item.cartItemId] && !trialIds.has(item.cartItemId))
      .reduce((sum, item) => {
        const product = skuMap.get(item.lineItem.sku)?.product;
        return product ? sum + getLocalizedPricing(product.pricingInCents, currency) * item.quantity : sum;
      }, 0);
  }, [cart, products, hardwareDiscounts, currency]);
}
